import { v4 as uuidv4 } from 'uuid'
import { useImageDB, type ImageRecord } from '../utils/imageDb'
import { getMaxAllowedPixelsFromProduct } from '../utils/productSizing'
import { createEditorPreview, createThumb, downscaleToFit, normalizeImageForUpload } from '../utils/imageOps'
import { detectCurrentUploadDeviceInfo } from '../utils/deviceInfo'

type ImportOpts = {
    product?: any
    productId?: number | null
    categoryId?: number | null
    projectCode?: string | null
    assetId?: string | null
    productTitle?: string | null
    categoryTitle?: string | null
    categoryThumbnail?: string | null
    dpi?: number
    outputMime?: string // optional: normalize to jpeg/webp later
    /** Tags/Alben, die alle Bilder dieses Imports bekommen */
    tags?: string[]
}

export async function importImageToDb(file: File, opts: ImportOpts = {}) {

    const db = useImageDB('imageDB')
    const id = uuidv4()
    const device = detectCurrentUploadDeviceInfo()

    const prepared = await normalizeImageForUpload(file, file.name, { quality: 0.92 })
    const mime = prepared.mime
    const sourceBlob = prepared.blob
    const origW = prepared.width
    const origH = prepared.height

    const maxPx = opts.product ? getMaxAllowedPixelsFromProduct(opts.product, opts.dpi ?? 300) : null

    let workingBlob = sourceBlob
    let finalW = origW
    let finalH = origH

    if (maxPx) {
        const r = await downscaleToFit(sourceBlob, maxPx.maxWidthPx, maxPx.maxHeightPx, opts.outputMime ?? mime)
        workingBlob = r.blob
        finalW = r.width
        finalH = r.height
    }

    const [thumbResult, editorPreviewResult] = await Promise.allSettled([
        createThumb(workingBlob, 280, 'image/jpeg', 0.86),
        createEditorPreview(workingBlob, 1600, 'image/webp', 0.82),
    ])
    const thumb = thumbResult.status === 'fulfilled' ? thumbResult.value : null
    const editorPreview = editorPreviewResult.status === 'fulfilled' ? editorPreviewResult.value : null

    const record: ImageRecord = {
        id,
        assetId: opts.assetId ?? null,
        name: prepared.name,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        mime: opts.outputMime ?? mime,
        bytes: workingBlob.size,
        width: finalW,
        height: finalH,
        originalBlob: workingBlob === sourceBlob ? undefined : sourceBlob,
        workingBlob,
        editorBlob: editorPreview?.blob,
        editorWidth: editorPreview?.width,
        editorHeight: editorPreview?.height,
        thumbBlob: thumb?.blob,
        thumbWidth: thumb?.width,
        thumbHeight: thumb?.height,
        productId: opts.productId ?? null,
        categoryId: opts.categoryId ?? null,
        ...(opts.projectCode ? { projectCode: opts.projectCode } : {}),
        ...(opts.productTitle ? ({ productTitle: String(opts.productTitle) } as any) : {}),
        ...(opts.categoryTitle ? ({ categoryTitle: String(opts.categoryTitle) } as any) : {}),
        ...(opts.categoryThumbnail ? ({ categoryThumbnail: String(opts.categoryThumbnail) } as any) : {}),
        ...(Array.isArray(opts.tags) && opts.tags.length ? { tags: [...opts.tags] } : {}),
        upload: {
            state: 'queued',
            tries: 0,
            updatedAt: Date.now(),
            source: 'desktop',
            deviceClass: device.deviceClass,
            deviceOs: device.deviceOs,
            deviceBrowser: device.deviceBrowser,
        },
    }

    await db.put(record)
    return record
}
